import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.lenis) window.lenis.scrollTo(el, { offset: -10, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/70 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => go("hero")}
          className="flex items-center gap-2.5 group"
        >
          <span className="w-3 h-3 rounded-full bg-brand group-hover:scale-125 transition-transform" />
          <span className="font-heading font-extrabold text-xl tracking-tight text-zinc-900">
            Bio<span className="text-brand">Soma</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href}`}
              onClick={() => go(l.href)}
              className="font-body text-sm text-zinc-600 hover:text-brand transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-brand hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-cta"
          onClick={() => go("contact")}
          className="hidden md:inline-flex items-center font-body text-sm font-semibold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full transition-colors"
        >
          Get in touch
        </button>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-zinc-900"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden backdrop-blur-xl bg-white/95 border-b border-black/5 px-6 py-6 flex flex-col gap-5"
          data-testid="mobile-menu"
        >
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-${l.href}`}
              onClick={() => go(l.href)}
              className="text-left font-body text-base text-zinc-700"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            className="text-left font-body text-base font-semibold text-brand"
          >
            Get in touch →
          </button>
        </motion.div>
      )}
    </motion.header>
  );
}
